import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// Roth IRA Calculator — compare Roth vs Taxable
export default function RothIRACalc() {
  const [balance, setBalance] = useState(30000);
  const [contrib, setContrib] = useState(7500);
  const [maximize, setMaximize] = useState('no');
  const [rate, setRate] = useState(6);
  const [age, setAge] = useState(30);
  const [retAge, setRetAge] = useState(65);
  const [marginal, setMarginal] = useState(25);
  const c = useMemo(() => {
    const years = Math.max(0, retAge - age);
    const r = rate / 100;
    const t = marginal / 100;
    // Roth IRA: contributions are after-tax; growth is tax-free.
    // Reference (Calculator.net): balance grows at full rate, contribution added each year.
    let roth = balance;                       // already after-tax
    let tax  = balance;                       // taxable brokerage: assumed after-tax dollars
    const rows = [];
    let principal = balance;
    for (let y = 1; y <= years; y++) {
      const curContrib = maximize === 'yes' ? (age + y >= 50 ? 8000 : 7000) : contrib;
      // Roth grows tax-free
      roth = roth * (1 + r) + curContrib;
      // Taxable: growth is taxed each year at marginal rate → net r*(1-t)
      tax  = tax  * (1 + r * (1 - t)) + curContrib;
      principal += curContrib;
      rows.push({ age: age + y, roth, tax, principal });
    }
    const rothInterest   = roth - principal;
    const taxableInterest = tax  - principal;
    // In taxable account tax is paid annually as we go; here we report cumulative tax paid on gains
    // (equivalent representation: rothInterest * t is what taxable HAS ALREADY paid)
    const totalTaxPaid = Math.max(0, rothInterest * t);
    return { roth, tax, totalPrincipal: principal, rothInterest, taxInterest: taxableInterest, totalTaxPaid, rows, diff: roth - tax };
  }, [balance, contrib, maximize, rate, age, retAge, marginal]);
  return (
    <CalcShell look="cnet" title="Roth IRA Calculator" subtitle="Compare a Roth IRA vs. a regular taxable account for U.S. residents."
      about={<><p>A <b>Roth IRA</b> uses after-tax contributions and grows tax-free. Compare it side-by-side with a taxable brokerage account.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Current balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={contrib} onChange={e=>setContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Maximize contributions?</label>
            <div className="flex gap-4 mt-1"><label className="flex items-center gap-1"><input type="radio" checked={maximize==='yes'} onChange={()=>setMaximize('yes')}/> Yes</label><label className="flex items-center gap-1"><input type="radio" checked={maximize==='no'} onChange={()=>setMaximize('no')}/> No</label></div>
          </div>
          <div className="field"><label>Expected rate of return</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Current age</label><input type="number" value={age} onChange={e=>setAge(+e.target.value||0)}/></div>
          <div className="field"><label>Retirement age</label><input type="number" value={retAge} onChange={e=>setRetAge(+e.target.value||0)}/></div>
          <div className="field"><label>Marginal tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={marginal} onChange={e=>setMarginal(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <table className="data-table">
            <thead><tr><th className="text-left">At age {retAge}</th><th className="text-right">Roth IRA</th><th className="text-right">Taxable account</th></tr></thead>
            <tbody>
              <tr><td>Balance</td><td className="text-right font-semibold text-blue-700" data-testid="roth-balance">{fmt(c.roth)}</td><td className="text-right">{fmt(c.tax)}</td></tr>
              <tr><td>Total principal</td><td className="text-right">{fmt(c.totalPrincipal)}</td><td className="text-right">{fmt(c.totalPrincipal)}</td></tr>
              <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.rothInterest)}</td><td className="text-right">{fmt(c.taxInterest)}</td></tr>
              <tr><td>Total tax</td><td className="text-right">$0</td><td className="text-right text-red-600">{fmt(c.totalTaxPaid)}</td></tr>
            </tbody>
          </table>
          <div className="mt-4 p-4 bg-green-50 rounded-lg text-sm text-slate-700">
            The Roth IRA can accumulate <b className="text-green-700">{fmt(Math.abs(c.diff))} more</b> than a regular taxable account by age {retAge}.
          </div>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Roth IRA vs Taxable — Balance Growth</h3>
        <BarChart
          data={c.rows.filter((_,i)=> i%Math.max(1,Math.floor(c.rows.length/12))===0 || i===c.rows.length-1).map(r => ({
            label: String(r.age),
            values: [
              { value: r.roth, color: '#059669' },
              { value: r.tax,  color: '#f97316' },
            ]
          }))}
          stack={false}
          legend={[
            { label: 'Roth IRA', color: '#059669' },
            { label: 'Taxable account', color: '#f97316' },
          ]}
        />
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Annual Schedule</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">Age</th><th className="px-3 py-2 text-right">Roth IRA</th><th className="px-3 py-2 text-right">Taxable account</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.age}</td><td className="px-3 py-2 text-right text-blue-700 font-semibold">{fmt(r.roth)}</td><td className="px-3 py-2 text-right">{fmt(r.tax)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
